import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatTravelDate } from '../../utils/dateUtils';
import QuoteRenderer from '../templates/QuoteRenderer';
import './ModernQuoteView.css';

// Create axios instance for public API calls
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
});

// Flight component's stops/returnStops fields (see FlightForm.js's
// STOPS_OPTIONS) are stored as a plain number of layovers - "3" doubles as
// "3+" there too, so mirror the same label here.
const formatStopsLabel = (stops) => {
  const n = parseInt(stops, 10);
  if (n === 0) return 'Nonstop';
  if (n === 1) return '1 Stop';
  if (n >= 2 && n < 3) return `${n} Stops`;
  if (n >= 3) return '3+ Stops';
  return null;
};

// "Layover in DEN (2h 26m), ORD" - connections come from
// FlightForm.js's connections/returnConnections fields, each entry
// { airport, duration } where duration is a free-text string (e.g. from
// importing a Duffel search result, or typed in manually).
const formatConnectionsText = (connections) => {
  if (!Array.isArray(connections) || connections.length === 0) return null;
  const parts = connections
    .filter((c) => c && c.airport)
    .map((c) => (c.duration ? `${c.airport} (${c.duration})` : c.airport));
  return parts.length > 0 ? `Layover in ${parts.join(', ')}` : null;
};

const ModernQuoteView = () => {
  const { quoteNumber, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Fallback: get token directly from localStorage if useAuth() token is not available
  const fallbackToken = token || localStorage.getItem('token');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [clientSelections, setClientSelections] = useState({});
  const [selectionsChanged, setSelectionsChanged] = useState(false);

  // Determine if this is agent preview mode
  const isAgentPreview = location.pathname.includes('/agent/quotes/') && id;
  const quoteIdentifier = isAgentPreview ? id : quoteNumber;
  

  // Component type detection for icons and styling
  const getComponentTypeInfo = (type) => {
    const typeStr = (type || '').toLowerCase();
    
    if (typeStr.includes('flight') || typeStr.includes('air')) {
      return { icon: '✈️', class: 'flight' };
    } else if (typeStr.includes('hotel') || typeStr.includes('accommodation') || typeStr.includes('resort')) {
      return { icon: '🏨', class: 'hotel' };
    } else if (typeStr.includes('car') || typeStr.includes('rental') || typeStr.includes('vehicle')) {
      return { icon: '🚗', class: 'car' };
    } else if (typeStr.includes('cruise') || typeStr.includes('ship')) {
      return { icon: '🚢', class: 'cruise' };
    } else if (typeStr.includes('tour') || typeStr.includes('excursion') || typeStr.includes('activity')) {
      return { icon: '🎯', class: 'tour' };
    } else if (typeStr.includes('insurance') || typeStr.includes('protection')) {
      return { icon: '🛡️', class: 'insurance' };
    } else if (typeStr.includes('transfer') || typeStr.includes('transport')) {
      return { icon: '🚐', class: 'transfer' };
    } else if (typeStr.includes('dining') || typeStr.includes('meal') || typeStr.includes('restaurant')) {
      return { icon: '🍽️', class: 'dining' };
    } else {
      return { icon: '🎫', class: 'other' };
    }
  };

  useEffect(() => {
    fetchQuote();
  }, [quoteIdentifier, isAgentPreview]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      
      let response;
      if (isAgentPreview) {
        // For agent preview, use the agent API directly to get full quote data
        const agentQuoteUrl = `${process.env.REACT_APP_API_URL || '/api'}/quotes/${quoteIdentifier}`;
        response = await axios.get(agentQuoteUrl, {
          headers: {
            'Authorization': `Bearer ${fallbackToken}`
          }
        });
      } else {
        // Use public API for client view (quoteIdentifier is already quote number)
        const publicUrl = `/public/quotes/${quoteIdentifier}`;
        response = await publicAxios.get(publicUrl);
      }
      
      // Parse JSON fields if they're strings (agent API returns strings, public API returns parsed objects)
      const quoteData = response.data;
      if (typeof quoteData.components === 'string') {
        try {
          quoteData.components = JSON.parse(quoteData.components);
        } catch (e) {
          console.warn('Failed to parse components JSON:', e);
          quoteData.components = [];
        }
      }
      if (typeof quoteData.options === 'string') {
        try {
          quoteData.options = JSON.parse(quoteData.options);
        } catch (e) {
          console.warn('Failed to parse options JSON:', e);
          quoteData.options = {};
        }
      }
      
      setQuote(quoteData);
      
      // Initialize client selections
      if (quoteData.components && Array.isArray(quoteData.components)) {
        const initialSelections = {};
        
        // Check if there are existing client selections
        let existingSelections = {};
        if (quoteData.options && quoteData.options.client_selections) {
          existingSelections = quoteData.options.client_selections;
        }
        
        quoteData.components.forEach(component => {
          if (component.id) {
            // Use existing selection if available, otherwise default behavior:
            // Required components are always selected, optional components start unselected
            if (existingSelections.hasOwnProperty(component.id)) {
              initialSelections[component.id] = existingSelections[component.id];
            } else {
              const isOption = component.isOption || component.data?.isOption || false;
              initialSelections[component.id] = !isOption;
            }
          }
        });
        setClientSelections(initialSelections);
        setSelectionsChanged(false); // Don't trigger save on initial load
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching quote:', err);
      setError(err.response?.data?.message || err.message || 'Error loading quote');
    } finally {
      setLoading(false);
    }
  };

  // Handle component selection changes
  const handleComponentSelection = (componentId, selected) => {
    setClientSelections(prev => ({
      ...prev,
      [componentId]: selected
    }));
    setSelectionsChanged(true);
  };

  // Calculate totals based on selected components
  const calculateSelectedTotals = () => {
    if (!quote || !Array.isArray(quote.components)) return { total: 0, deposit: 0 };
    
    let selectedTotal = 0;
    let selectedDeposit = 0;
    
    quote.components.forEach(component => {
      const isOption = component.isOption || component.data?.isOption || false;
      const isSelected = clientSelections[component.id];
      const shouldInclude = !isOption || isSelected;
      
      if (shouldInclude) {
        const componentPrice = parseFloat(component.price) || 0;
        const paymentTiming = component.paymentTiming || component.data?.paymentTiming;
        
        // Handle optional components with payment timing
        if (isOption && paymentTiming) {
          if (paymentTiming === 'immediate') {
            // Pay full price when selected - add to both total and deposit
            selectedTotal += componentPrice;
            selectedDeposit += componentPrice;
          } else if (paymentTiming === 'with_deposit') {
            // Pay deposit when selected - add full price to total, deposit to deposit
            selectedTotal += componentPrice;
            const depositAmt = parseFloat(component.optionalDepositAmount || component.data?.optionalDepositAmount) || 0;
            selectedDeposit += depositAmt;
          } else if (paymentTiming === 'with_final') {
            // Pay with final payment - add to total but not deposit
            selectedTotal += componentPrice;
          } else if (paymentTiming === 'bundled') {
            // Already included - don't add to total or deposit
          }
        } else {
          // Required component or optional without payment timing
          selectedTotal += componentPrice;
          
          // Calculate deposit. An explicit depositAmount/deposit_due of 0 is a
          // valid, intentional value (e.g. supplier allows a card-guarantee-only
          // booking with no payment due today) and must not fall through to the
          // full-price fallback below - only fall through when no deposit
          // amount was specified at all.
          const isExplicitAmount = (value) => value !== null && value !== undefined && value !== '' && !isNaN(parseFloat(value));
          if (component.depositRequired && isExplicitAmount(component.depositAmount)) {
            selectedDeposit += parseFloat(component.depositAmount);
          } else if (component.data && isExplicitAmount(component.data.deposit_due)) {
            selectedDeposit += parseFloat(component.data.deposit_due);
          } else {
            // No deposit specified, use full component price
            selectedDeposit += componentPrice;
          }
        }
      }
    });
    
    return { total: selectedTotal, deposit: selectedDeposit };
  };

  // Save client selections to backend
  const saveClientSelections = async () => {
    if (!selectionsChanged || isAgentPreview) return;

    try {
      const response = await publicAxios.post(`/public/quotes/${quoteIdentifier}/selections`, {
        selections: clientSelections
      });
      
      if (response.data.success) {
        setSelectionsChanged(false);
        console.log('Selections saved successfully:', response.data);
      }
    } catch (error) {
      console.error('Error saving selections:', error);
      // Don't show error to user as this is background save
    }
  };

  // Auto-save selections when they change (debounced)
  useEffect(() => {
    if (!selectionsChanged) return;

    const timeoutId = setTimeout(() => {
      saveClientSelections();
    }, 2000); // Save 2 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [clientSelections, selectionsChanged]);

  const handlePayDeposit = async () => {
    // Only allow payment for public quotes (not agent preview)
    if (isAgentPreview) {
      alert('Payment is not available in agent preview mode');
      return;
    }

    // Save current selections before proceeding to payment
    if (selectionsChanged) {
      await saveClientSelections();
    }

    try {
      const response = await publicAxios.post(`/public/quotes/${quoteIdentifier}/payment`, {
        paymentType: 'deposit'
      });
      
      if (response.data.success) {
        // Navigate to payment page
        window.location.href = response.data.paymentUrl;
      }
    } catch (err) {
      console.error('Error initiating payment:', err);
      alert('Error initiating payment. Please try again.');
    }
  };

  const handleContactAgent = () => {
    // Only allow contact for public quotes (not agent preview)
    if (isAgentPreview) {
      alert('Contact feature is not available in agent preview mode');
      return;
    }
    setShowContactModal(true);
  };

  const sendContactMessage = async () => {
    if (!contactMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    try {
      setSendingMessage(true);
      await publicAxios.post(`/public/quotes/${quoteIdentifier}/contact`, {
        message: contactMessage
      });
      alert('Your message has been sent to the agent.');
      setShowContactModal(false);
      setContactMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const downloadPDF = () => {
    // Ensure quote data is available and has quote_number
    if (!quote || !quote.quote_number) {
      alert('Unable to generate PDF. Quote information not available.');
      return;
    }
    // Use quote.quote_number instead of quoteIdentifier to ensure compatibility
    // Backend PDF endpoint expects quote_number, not ID
    window.open(`/api/public/quotes/${quote.quote_number}/pdf`, '_blank');
  };

  const downloadAttachment = (fileId, fileName) => {
    window.open(`/api/public/files/${fileId}/download`, '_blank');
  };

  const formatPrice = (price) => {
    return parseFloat(price || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '';
    const start = formatTravelDate(startDate);
    if (!endDate) return start;
    const end = formatTravelDate(endDate);
    return `${start} - ${end}`;
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Calculate inclusive days (add 1 to include both start and end dates)
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const nights = diffDays - 1; // Nights = Days - 1
    
    return `${diffDays} Day${diffDays > 1 ? 's' : ''}${nights > 0 ? ', ' + nights + ' Night' + (nights > 1 ? 's' : '') : ''}`;
  };

  const getComponentDetails = (component) => {
    const details = [];
    
    // Component Description (for non-package components)
    if (component.description && component.type !== 'package') {
      details.push({ label: 'Description', value: component.description });
    }
    
    // Package Deal Details - Comprehensive display of all form fields
    if (component.type === 'package') {
      const packageData = component.data || {};
      
      // Package Type Display
      if (packageData.packageType) {
        const packageTypeLabels = {
          'hotel-flight': 'Hotel + Flight Package',
          'hotel-car': 'Hotel + Car Rental Package', 
          'hotel-activity': 'Hotel + Activity Package'
        };
        const packageLabel = packageTypeLabels[packageData.packageType] || packageData.packageType;
        details.push({ label: 'Package Type', value: packageLabel });
      }

      // Hotel Information (always present in package deals)
      if (packageData.propertyName) {
        details.push({ label: 'Property', value: packageData.propertyName });
      }
      if (packageData.hotelLocation) {
        details.push({ label: 'Location', value: packageData.hotelLocation });
      }
      if (packageData.checkInDate && packageData.checkOutDate) {
        const checkInText = packageData.checkInTime ? 
          `${formatTravelDate(packageData.checkInDate)}, ${packageData.checkInTime}` : 
          formatTravelDate(packageData.checkInDate);
        const checkOutText = packageData.checkOutTime ? 
          `${formatTravelDate(packageData.checkOutDate)}, ${packageData.checkOutTime}` : 
          formatTravelDate(packageData.checkOutDate);
        details.push({ label: 'Check-in', value: checkInText });
        details.push({ label: 'Check-out', value: checkOutText });
      } else if (packageData.checkInDate) {
        const checkInText = packageData.checkInTime ? 
          `${formatTravelDate(packageData.checkInDate)}, ${packageData.checkInTime}` : 
          formatTravelDate(packageData.checkInDate);
        details.push({ label: 'Check-in', value: checkInText });
      } else if (packageData.checkOutDate) {
        const checkOutText = packageData.checkOutTime ? 
          `${formatTravelDate(packageData.checkOutDate)}, ${packageData.checkOutTime}` : 
          formatTravelDate(packageData.checkOutDate);
        details.push({ label: 'Check-out', value: checkOutText });
      }
      if (packageData.roomType) {
        details.push({ label: 'Room Type', value: packageData.roomType });
      }

      // Flight Information (for packages including flight)
      if (packageData.packageType && packageData.packageType.includes('flight')) {
        if (packageData.airline) {
          const airlineText = packageData.flightNumber ? 
            `${packageData.airline} (${packageData.flightNumber})` : 
            packageData.airline;
          details.push({ label: 'Airline', value: airlineText });
        }
        if (packageData.departureAirport && packageData.arrivalAirport) {
          details.push({ label: 'Route', value: `${packageData.departureAirport} → ${packageData.arrivalAirport}` });
        }
      }

      // Car Rental Information (for packages including car)
      if (packageData.packageType && packageData.packageType.includes('car')) {
        if (packageData.rentalAgency) {
          details.push({ label: 'Rental Agency', value: packageData.rentalAgency });
        }
        if (packageData.carClass) {
          details.push({ label: 'Vehicle Class', value: packageData.carClass });
        }
        if (packageData.pickupLocation) {
          details.push({ label: 'Pickup Location', value: packageData.pickupLocation });
        }
        if (packageData.dropoffLocation) {
          details.push({ label: 'Drop-off Location', value: packageData.dropoffLocation });
        }
      }

      // Activity Information (for packages including activity)
      if (packageData.packageType && packageData.packageType.includes('activity')) {
        if (packageData.activityName) {
          details.push({ label: 'Activity', value: packageData.activityName });
        }
        if (packageData.activityDate && packageData.activityTime) {
          details.push({ label: 'Activity Date & Time', value: `${formatTravelDate(packageData.activityDate)}, ${packageData.activityTime}` });
        } else if (packageData.activityDate) {
          details.push({ label: 'Activity Date', value: formatTravelDate(packageData.activityDate) });
        }
        if (packageData.activityLocation) {
          details.push({ label: 'Activity Location', value: packageData.activityLocation });
        }
      }

      // Booking Details
      if (packageData.confirmationNumber) {
        details.push({ label: 'Confirmation Number', value: packageData.confirmationNumber });
      }
      
      // Deposit Information
      if (component.depositRequired && component.depositAmount && component.depositAmount > 0) {
        const depositText = component.depositDueDate ? 
          `$${parseFloat(component.depositAmount).toFixed(2)} (Due: ${formatTravelDate(component.depositDueDate)})` : 
          `$${parseFloat(component.depositAmount).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }

      // Package Inclusions & Notes
      if (component.description) {
        details.push({ label: 'Package Inclusions', value: component.description });
      }
    }

    // General Item Details - Comprehensive display of all form fields
    if (component.type === 'general') {
      const generalData = component.data || {};
      
      // Item Information
      if (generalData.itemName) {
        details.push({ label: 'Item Name', value: generalData.itemName });
      }
      if (generalData.category) {
        details.push({ label: 'Category', value: generalData.category });
      }
      
      // Quantity and Pricing
      if (generalData.quantity && generalData.quantity > 1) {
        details.push({ label: 'Quantity', value: generalData.quantity });
      }
      if (generalData.unitPrice) {
        const unitPriceText = generalData.quantity && generalData.quantity > 1 ? 
          `$${parseFloat(generalData.unitPrice).toFixed(2)} per item` : 
          `$${parseFloat(generalData.unitPrice).toFixed(2)}`;
        details.push({ label: 'Unit Price', value: unitPriceText });
      }
      
      // Supplier and Booking Information
      if (generalData.supplier) {
        details.push({ label: 'Supplier/Provider', value: generalData.supplier });
      }
      if (generalData.confirmationNumber) {
        details.push({ label: 'Confirmation Number', value: generalData.confirmationNumber });
      }
      if (generalData.date) {
        details.push({ label: 'Valid/Use Date', value: formatTravelDate(generalData.date) });
      }
      
      // Item Description
      if (generalData.description) {
        details.push({ label: 'Item Details', value: generalData.description });
      }
    }

    // Legacy Package Type (for other components with packageType but not type='package')
    if (component.packageType && component.type !== 'package') {
      const packageTypeLabels = {
        'hotel-flight': 'Hotel + Flight Package',
        'hotel-car': 'Hotel + Car Rental Package', 
        'hotel-activity': 'Hotel + Activity Package',
        'hotel-flight-car': 'Hotel + Flight + Car Rental Package',
        'hotel-flight-activity': 'Hotel + Flight + Activity Package',
        'hotel-car-activity': 'Hotel + Car + Activity Package',
        'hotel-flight-car-activity': 'Complete Package (Hotel + Flight + Car + Activity)',
        'flight-car': 'Flight + Car Rental Package'
      };
      const packageLabel = packageTypeLabels[component.packageType] || component.packageType;
      details.push({ label: 'Package Type', value: packageLabel });
    }

    // Flight Details - Comprehensive display of all form fields
    if (component.type === 'flight') {
      const flightData = component.data || {};
      
      if (flightData.airline) {
        details.push({ label: 'Airline', value: `${flightData.airline}${flightData.flightNumber ? ` (${flightData.flightNumber})` : ''}` });
      }
      const isRoundTrip = flightData.tripType === 'round_trip';
      if (flightData.departureAirport && flightData.arrivalAirport) {
        details.push({ label: isRoundTrip ? 'Outbound Route' : 'Route', value: `${flightData.departureAirport} → ${flightData.arrivalAirport}` });
      }
      if (flightData.stops !== undefined && flightData.stops !== null && flightData.stops !== '') {
        details.push({ label: isRoundTrip ? 'Outbound Stops' : 'Stops', value: formatStopsLabel(flightData.stops) });
      }
      const outboundConnectionsText = formatConnectionsText(flightData.connections);
      if (outboundConnectionsText) {
        details.push({ label: isRoundTrip ? 'Outbound Layover' : 'Layover', value: outboundConnectionsText });
      }
      if (flightData.departureDate) {
        details.push({ label: isRoundTrip ? 'Outbound Departure' : 'Departure', value: `${formatTravelDate(flightData.departureDate)}${flightData.departureTime ? `, ${flightData.departureTime}` : ''}` });
      }
      if (flightData.arrivalDate) {
        details.push({ label: isRoundTrip ? 'Outbound Arrival' : 'Arrival', value: `${formatTravelDate(flightData.arrivalDate)}${flightData.arrivalTime ? `, ${flightData.arrivalTime}` : ''}` });
      }
      if (flightData.seats) {
        details.push({ label: isRoundTrip ? 'Outbound Seats' : 'Seats', value: flightData.seats });
      }
      if (flightData.confirmationNumber) {
        details.push({ label: isRoundTrip ? 'Outbound Confirmation Number' : 'Confirmation Number', value: flightData.confirmationNumber });
      }

      // Return leg (round-trip only) - kept as its own set of rows, not
      // merged into the outbound ones above, since the two legs can have
      // different airlines/flight numbers/PNRs.
      if (isRoundTrip) {
        if (flightData.returnDepartureAirport && flightData.returnArrivalAirport) {
          const returnAirlineText = (flightData.returnAirline || flightData.returnFlightNumber)
            ? ` (${[flightData.returnAirline || flightData.airline, flightData.returnFlightNumber].filter(Boolean).join(' ')})`
            : '';
          details.push({ label: 'Return Route', value: `${flightData.returnDepartureAirport} → ${flightData.returnArrivalAirport}${returnAirlineText}` });
        }
        if (flightData.returnStops !== undefined && flightData.returnStops !== null && flightData.returnStops !== '') {
          details.push({ label: 'Return Stops', value: formatStopsLabel(flightData.returnStops) });
        }
        const returnConnectionsText = formatConnectionsText(flightData.returnConnections);
        if (returnConnectionsText) {
          details.push({ label: 'Return Layover', value: returnConnectionsText });
        }
        if (flightData.returnDepartureDate) {
          details.push({ label: 'Return Departure', value: `${formatTravelDate(flightData.returnDepartureDate)}${flightData.returnDepartureTime ? `, ${flightData.returnDepartureTime}` : ''}` });
        }
        if (flightData.returnArrivalDate) {
          details.push({ label: 'Return Arrival', value: `${formatTravelDate(flightData.returnArrivalDate)}${flightData.returnArrivalTime ? `, ${flightData.returnArrivalTime}` : ''}` });
        }
        if (flightData.returnSeats) {
          details.push({ label: 'Return Seats', value: flightData.returnSeats });
        }
        if (flightData.returnConfirmationNumber) {
          details.push({ label: 'Return Confirmation Number', value: flightData.returnConfirmationNumber });
        }
      }
      if (flightData.liveTracking) {
        details.push({ label: 'Live Tracking', value: 'Enabled' });
      }
    }

    // Hotel Details - Comprehensive display of all form fields
    if (component.type === 'hotel') {
      const hotelData = component.data || {};
      
      if (hotelData.propertyName) {
        details.push({ label: 'Property', value: hotelData.propertyName });
      }
      if (hotelData.location) {
        details.push({ label: 'Location', value: hotelData.location });
      }
      // Handle hotelLocation for package deals (different field name)
      if (hotelData.hotelLocation && !hotelData.location) {
        details.push({ label: 'Location', value: hotelData.hotelLocation });
      }
      if (hotelData.roomType) {
        details.push({ label: 'Room Type', value: hotelData.roomType });
      }
      if (hotelData.checkInDate) {
        const checkInText = hotelData.checkInTime ? 
          `${formatTravelDate(hotelData.checkInDate)}, ${hotelData.checkInTime}` : 
          formatTravelDate(hotelData.checkInDate);
        details.push({ label: 'Check-in', value: checkInText });
      }
      if (hotelData.checkOutDate) {
        const checkOutText = hotelData.checkOutTime ? 
          `${formatTravelDate(hotelData.checkOutDate)}, ${hotelData.checkOutTime}` : 
          formatTravelDate(hotelData.checkOutDate);
        details.push({ label: 'Check-out', value: checkOutText });
      }
      if (hotelData.phoneNumber) {
        details.push({ label: 'Phone', value: hotelData.phoneNumber });
      }
      if (hotelData.confirmationNumber) {
        details.push({ label: 'Confirmation Number', value: hotelData.confirmationNumber });
      }
    }

    // Car Rental Details - Comprehensive display of all form fields
    if (component.type === 'car') {
      const carData = component.data || {};
      
      if (carData.agencyName) {
        details.push({ label: 'Rental Agency', value: carData.agencyName });
      }
      // Legacy field support
      if (carData.rentalAgency && !carData.agencyName) {
        details.push({ label: 'Rental Agency', value: carData.rentalAgency });
      }
      if (carData.carClass) {
        details.push({ label: 'Vehicle Class', value: carData.carClass });
      }
      if (carData.rentalDetails) {
        details.push({ label: 'Rental Details', value: carData.rentalDetails });
      }
      if (carData.carSeats && carData.carSeats > 0) {
        details.push({ label: 'Car Seats', value: `${carData.carSeats} seat${carData.carSeats > 1 ? 's' : ''}` });
      }
      if (carData.pickupLocation) {
        details.push({ label: 'Pickup Location', value: carData.pickupLocation });
      }
      if (carData.pickupDate && carData.pickupTime) {
        details.push({ label: 'Pickup', value: `${formatTravelDate(carData.pickupDate)}, ${carData.pickupTime}` });
      } else if (carData.pickupDate) {
        details.push({ label: 'Pickup Date', value: formatTravelDate(carData.pickupDate) });
      }
      if (carData.dropoffLocation) {
        details.push({ label: 'Drop-off Location', value: carData.dropoffLocation });
      }
      if (carData.dropoffDate && carData.dropoffTime) {
        details.push({ label: 'Drop-off', value: `${formatTravelDate(carData.dropoffDate)}, ${carData.dropoffTime}` });
      } else if (carData.dropoffDate) {
        details.push({ label: 'Drop-off Date', value: formatTravelDate(carData.dropoffDate) });
      }
      if (carData.confirmationNumber) {
        details.push({ label: 'Confirmation Number', value: carData.confirmationNumber });
      }
      if (carData.phoneNumber) {
        details.push({ label: 'Contact Phone', value: carData.phoneNumber });
      }
    }

    // Restaurant Details
    if (component.restaurantName) {
      details.push({ label: 'Restaurant', value: component.restaurantName });
    }
    if (component.partySize) {
      details.push({ label: 'Party Size', value: component.partySize });
    }
    if (component.reservationTime) {
      details.push({ label: 'Reservation Time', value: component.reservationTime });
    }

    // Activity Details - Comprehensive display of all form fields
    if (component.type === 'activity') {
      const activityData = component.data || {};
      
      if (activityData.activityType) {
        details.push({ label: 'Activity Type', value: activityData.activityType });
      }
      if (activityData.activityName) {
        details.push({ label: 'Activity/Location Name', value: activityData.activityName });
      }
      if (activityData.address) {
        details.push({ label: 'Address', value: activityData.address });
      }
      if (activityData.details) {
        details.push({ label: 'Activity Details', value: activityData.details });
      }
      if (activityData.date && activityData.time) {
        details.push({ label: 'Date & Time', value: `${formatTravelDate(activityData.date)}, ${activityData.time}` });
      } else if (activityData.date) {
        details.push({ label: 'Date', value: formatTravelDate(activityData.date) });
      } else if (activityData.time) {
        details.push({ label: 'Time', value: activityData.time });
      }
      if (activityData.duration) {
        details.push({ label: 'Duration', value: activityData.duration });
      }
      
      // Guest information
      if (activityData.adults || activityData.children) {
        let guestText = '';
        if (activityData.adults) {
          guestText += `${activityData.adults} Adult${activityData.adults > 1 ? 's' : ''}`;
        }
        if (activityData.children) {
          if (guestText) guestText += ', ';
          guestText += `${activityData.children} Child${activityData.children > 1 ? 'ren' : ''}`;
          
          // Add children ages if available
          if (activityData.childrenAges && activityData.childrenAges.length > 0) {
            const ages = activityData.childrenAges.filter(age => age !== null && age !== undefined && age !== '');
            if (ages.length > 0) {
              guestText += ` (Ages: ${ages.join(', ')})`;
            }
          }
        }
        if (guestText) {
          details.push({ label: 'Guests', value: guestText });
        }
      }
      
      if (activityData.confirmationNumber) {
        details.push({ label: 'Confirmation Number', value: activityData.confirmationNumber });
      }
      if (activityData.phoneNumber) {
        details.push({ label: 'Contact Phone', value: activityData.phoneNumber });
      }
      
      // Legacy fields for backward compatibility
      if (activityData.meetingPoint) {
        details.push({ label: 'Meeting Point', value: activityData.meetingPoint });
      }
      if (activityData.activityLocation) {
        details.push({ label: 'Activity Location', value: activityData.activityLocation });
      }
      // Handle activityDate and activityTime for package deals
      if (activityData.activityDate && activityData.activityTime) {
        details.push({ label: 'Activity Date & Time', value: `${formatTravelDate(activityData.activityDate)}, ${activityData.activityTime}` });
      } else if (activityData.activityDate) {
        details.push({ label: 'Activity Date', value: formatTravelDate(activityData.activityDate) });
      }
    }

    // Cruise Details - Comprehensive display of all form fields
    if (component.type === 'cruise') {
      const cruiseData = component.data || {};
      
      if (cruiseData.carrier) {
        details.push({ label: 'Cruise Line', value: cruiseData.carrier });
      }
      if (cruiseData.ship) {
        details.push({ label: 'Ship', value: cruiseData.ship });
      }
      if (cruiseData.stateroom) {
        details.push({ label: 'Stateroom Type', value: cruiseData.stateroom });
      }
      if (cruiseData.departsFrom) {
        details.push({ label: 'Departs From', value: cruiseData.departsFrom });
      }
      if (cruiseData.departureDate && cruiseData.departureTime) {
        details.push({ label: 'Departure', value: `${formatTravelDate(cruiseData.departureDate)}, ${cruiseData.departureTime}` });
      } else if (cruiseData.departureDate) {
        details.push({ label: 'Departure Date', value: formatTravelDate(cruiseData.departureDate) });
      }
      if (cruiseData.arrivesIn) {
        details.push({ label: 'Arrives In', value: cruiseData.arrivesIn });
      }
      if (cruiseData.arrivalDate && cruiseData.arrivalTime) {
        details.push({ label: 'Arrival', value: `${formatTravelDate(cruiseData.arrivalDate)}, ${cruiseData.arrivalTime}` });
      } else if (cruiseData.arrivalDate) {
        details.push({ label: 'Arrival Date', value: formatTravelDate(cruiseData.arrivalDate) });
      }
      if (cruiseData.numberOfPorts) {
        details.push({ label: 'Number of Ports', value: cruiseData.numberOfPorts });
      }
      if (cruiseData.confirmationNumber) {
        details.push({ label: 'Confirmation Number', value: cruiseData.confirmationNumber });
      }
      
      // Legacy fields for backward compatibility
      if (cruiseData.deck) {
        details.push({ label: 'Deck', value: cruiseData.deck });
      }
      if (cruiseData.diningTime) {
        details.push({ label: 'Dining Time', value: cruiseData.diningTime });
      }
    }

    // Insurance Details - Access data from component.data object
    if (component.type === 'insurance') {
      const insuranceData = component.data || {};
      
      if (insuranceData.insuranceType) {
        details.push({ label: 'Insurance Type', value: insuranceData.insuranceType });
      }
      if (insuranceData.provider) {
        details.push({ label: 'Insurance Provider', value: insuranceData.provider });
      }
      if (insuranceData.policyNumber) {
        details.push({ label: 'Policy Number', value: insuranceData.policyNumber });
      }
      if (insuranceData.coverage) {
        details.push({ label: 'Coverage', value: insuranceData.coverage });
      }
      if (insuranceData.policyDetails) {
        details.push({ label: 'Policy Details', value: insuranceData.policyDetails });
      }
      if (insuranceData.coverageAmounts) {
        details.push({ label: 'Coverage Amounts', value: insuranceData.coverageAmounts });
      }
      if (insuranceData.deductibles) {
        details.push({ label: 'Deductibles', value: insuranceData.deductibles });
      }
      if (insuranceData.beneficiaries) {
        details.push({ label: 'Beneficiaries', value: insuranceData.beneficiaries });
      }
      if (insuranceData.providerPhone) {
        details.push({ label: 'Provider Phone', value: insuranceData.providerPhone });
      }
      if (insuranceData.policyStartDate) {
        details.push({ label: 'Policy Start', value: formatTravelDate(insuranceData.policyStartDate) });
      }
      if (insuranceData.policyEndDate) {
        details.push({ label: 'Policy End', value: formatTravelDate(insuranceData.policyEndDate) });
      }
      if (insuranceData.premium) {
        details.push({ label: 'Premium', value: `$${parseFloat(insuranceData.premium).toFixed(2)}` });
      }
      if (insuranceData.additionalNotes) {
        details.push({ label: 'Additional Notes', value: insuranceData.additionalNotes });
      }
    }

    // Disney World Resort Package Details - Comprehensive display of all form fields
    if (component.type === 'disney-wdw-resort-package') {
      const disneyData = component.data || {};
      
      // Resort Accommodation Details
      if (disneyData.resort_name) {
        details.push({ label: 'Resort', value: disneyData.resort_name });
      }
      if (disneyData.room_type) {
        details.push({ label: 'Room Type', value: disneyData.room_type });
      }
      if (disneyData.view) {
        details.push({ label: 'View', value: disneyData.view });
      }
      if (disneyData.check_in_date && disneyData.check_out_date) {
        details.push({ 
          label: 'Stay Dates', 
          value: `${formatTravelDate(disneyData.check_in_date)} to ${formatTravelDate(disneyData.check_out_date)}` 
        });
      } else if (disneyData.check_in_date) {
        details.push({ label: 'Check-in', value: formatTravelDate(disneyData.check_in_date) });
      } else if (disneyData.check_out_date) {
        details.push({ label: 'Check-out', value: formatTravelDate(disneyData.check_out_date) });
      }
      
      // Theme Park Tickets
      if (disneyData.ticket_type) {
        details.push({ label: 'Ticket Type', value: disneyData.ticket_type });
      }
      if (disneyData.ticket_options) {
        details.push({ label: 'Ticket Options', value: disneyData.ticket_options });
      }
      if (disneyData.ticket_validity_start && disneyData.ticket_validity_end) {
        details.push({ 
          label: 'Ticket Validity', 
          value: `${formatTravelDate(disneyData.ticket_validity_start)} to ${formatTravelDate(disneyData.ticket_validity_end)}` 
        });
      } else if (disneyData.ticket_validity_start) {
        details.push({ label: 'Ticket Valid From', value: formatTravelDate(disneyData.ticket_validity_start) });
      } else if (disneyData.ticket_validity_end) {
        details.push({ label: 'Ticket Valid Until', value: formatTravelDate(disneyData.ticket_validity_end) });
      }
      
      // Add-on Services
      if (disneyData.dining_plan && disneyData.dining_plan !== 'No Dining Plan') {
        details.push({ label: 'Dining Plan', value: disneyData.dining_plan });
      }
      if (disneyData.memory_maker && disneyData.memory_maker === 'Yes') {
        details.push({ label: 'Memory Maker', value: 'Included' });
      }
      if (disneyData.ground_transportation) {
        details.push({ label: 'Ground Transportation', value: disneyData.ground_transportation });
      }
      if (disneyData.travel_protection && disneyData.travel_protection === 'Included') {
        const protectionText = disneyData.travel_protection_price ? 
          `Included ($${parseFloat(disneyData.travel_protection_price).toFixed(2)})` : 
          'Included';
        details.push({ label: 'Travel Protection', value: protectionText });
      }
      
      // Pricing & Payment Information
      if (disneyData.total_price_due_by) {
        details.push({ label: 'Total Due By', value: formatTravelDate(disneyData.total_price_due_by) });
      }
      if (disneyData.deposit_due && disneyData.deposit_due > 0) {
        const depositText = disneyData.deposit_due_by ? 
          `$${parseFloat(disneyData.deposit_due).toFixed(2)} (Due: ${formatTravelDate(disneyData.deposit_due_by)})` : 
          `$${parseFloat(disneyData.deposit_due).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }
      
      // Special Requests
      if (disneyData.special_requests) {
        details.push({ label: 'Special Requests', value: disneyData.special_requests });
      }
      
      // Children Information (if available)
      if (disneyData.children && disneyData.children.length > 0) {
        const childrenInfo = disneyData.children.map(child => `Age ${child.age}`).join(', ');
        details.push({ label: 'Children', value: childrenInfo });
      }
    }

    // Disneyland Resort Package Details - Comprehensive display of all form fields
    if (component.type === 'disneyland-resort-package') {
      const disneylandData = component.data || {};
      
      // Resort Accommodation Details
      if (disneylandData.resort_name) {
        details.push({ label: 'Resort/Hotel', value: disneylandData.resort_name });
      }
      if (disneylandData.room_type) {
        details.push({ label: 'Room Type', value: disneylandData.room_type });
      }
      if (disneylandData.view) {
        details.push({ label: 'View', value: disneylandData.view });
      }
      if (disneylandData.check_in_date && disneylandData.check_out_date) {
        details.push({ 
          label: 'Stay Dates', 
          value: `${formatTravelDate(disneylandData.check_in_date)} to ${formatTravelDate(disneylandData.check_out_date)}` 
        });
      } else if (disneylandData.check_in_date) {
        details.push({ label: 'Check-in', value: formatTravelDate(disneylandData.check_in_date) });
      } else if (disneylandData.check_out_date) {
        details.push({ label: 'Check-out', value: formatTravelDate(disneylandData.check_out_date) });
      }
      
      // Theme Park Tickets
      if (disneylandData.ticket_type) {
        details.push({ label: 'Ticket Type', value: disneylandData.ticket_type });
      }
      if (disneylandData.ticket_options) {
        details.push({ label: 'Ticket Options', value: disneylandData.ticket_options });
      }
      if (disneylandData.ticket_validity_start && disneylandData.ticket_validity_end) {
        details.push({ 
          label: 'Ticket Validity', 
          value: `${formatTravelDate(disneylandData.ticket_validity_start)} to ${formatTravelDate(disneylandData.ticket_validity_end)}` 
        });
      } else if (disneylandData.ticket_validity_start) {
        details.push({ label: 'Ticket Valid From', value: formatTravelDate(disneylandData.ticket_validity_start) });
      } else if (disneylandData.ticket_validity_end) {
        details.push({ label: 'Ticket Valid Until', value: formatTravelDate(disneylandData.ticket_validity_end) });
      }
      
      // Add-on Services (no dining plan or Memory Maker for Disneyland)
      if (disneylandData.ground_transportation) {
        details.push({ label: 'Ground Transportation', value: disneylandData.ground_transportation });
      }
      if (disneylandData.travel_protection && disneylandData.travel_protection === 'Included') {
        const protectionText = disneylandData.travel_protection_price ? 
          `Included ($${parseFloat(disneylandData.travel_protection_price).toFixed(2)})` : 
          'Included';
        details.push({ label: 'Travel Protection', value: protectionText });
      }
      
      // Pricing & Payment Information
      if (disneylandData.total_price_due_by) {
        details.push({ label: 'Total Due By', value: formatTravelDate(disneylandData.total_price_due_by) });
      }
      if (disneylandData.deposit_due && disneylandData.deposit_due > 0) {
        const depositText = disneylandData.deposit_due_by ? 
          `$${parseFloat(disneylandData.deposit_due).toFixed(2)} (Due: ${formatTravelDate(disneylandData.deposit_due_by)})` : 
          `$${parseFloat(disneylandData.deposit_due).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }
      
      // Special Requests
      if (disneylandData.special_requests) {
        details.push({ label: 'Special Requests', value: disneylandData.special_requests });
      }
      
      // Children Information (if available)
      if (disneylandData.children && disneylandData.children.length > 0) {
        const childrenInfo = disneylandData.children.map(child => `Age ${child.age}`).join(', ');
        details.push({ label: 'Children', value: childrenInfo });
      }
    }

    // Universal Orlando Package Details - Comprehensive display of all form fields
    if (component.type === 'universal-orlando-package') {
      const universalData = component.data || {};
      
      // Resort Accommodation Details
      if (universalData.resort_name) {
        details.push({ label: 'Resort/Hotel', value: universalData.resort_name });
      }
      if (universalData.room_type) {
        details.push({ label: 'Room Type', value: universalData.room_type });
      }
      if (universalData.view) {
        details.push({ label: 'View', value: universalData.view });
      }
      if (universalData.check_in_date && universalData.check_out_date) {
        details.push({ 
          label: 'Stay Dates', 
          value: `${formatTravelDate(universalData.check_in_date)} to ${formatTravelDate(universalData.check_out_date)}` 
        });
      } else if (universalData.check_in_date) {
        details.push({ label: 'Check-in', value: formatTravelDate(universalData.check_in_date) });
      } else if (universalData.check_out_date) {
        details.push({ label: 'Check-out', value: formatTravelDate(universalData.check_out_date) });
      }
      
      // Theme Park Tickets
      if (universalData.ticket_type) {
        details.push({ label: 'Ticket Type', value: universalData.ticket_type });
      }
      if (universalData.express_pass && universalData.express_pass !== 'None') {
        details.push({ label: 'Express Pass', value: universalData.express_pass });
      }
      if (universalData.early_park_admission && universalData.early_park_admission === 'Included') {
        details.push({ label: 'Early Park Admission', value: 'Included' });
      }
      
      // Dining & Entertainment
      if (universalData.dining_plan && universalData.dining_plan !== 'No Dining Plan') {
        details.push({ label: 'Dining Plan', value: universalData.dining_plan });
      }
      if (universalData.citywalk_venues) {
        details.push({ label: 'CityWalk Venues', value: universalData.citywalk_venues });
      }
      
      // Add-on Services
      if (universalData.ground_transportation) {
        details.push({ label: 'Ground Transportation', value: universalData.ground_transportation });
      }
      if (universalData.travel_protection && universalData.travel_protection === 'Included') {
        const protectionText = universalData.travel_protection_price ? 
          `Included ($${parseFloat(universalData.travel_protection_price).toFixed(2)})` : 
          'Included';
        details.push({ label: 'Travel Protection', value: protectionText });
      }
      
      // Pricing & Payment Information
      if (universalData.total_price_due_by) {
        details.push({ label: 'Total Due By', value: formatTravelDate(universalData.total_price_due_by) });
      }
      if (universalData.deposit_due && universalData.deposit_due > 0) {
        const depositText = universalData.deposit_due_by ? 
          `$${parseFloat(universalData.deposit_due).toFixed(2)} (Due: ${formatTravelDate(universalData.deposit_due_by)})` : 
          `$${parseFloat(universalData.deposit_due).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }
      
      // Special Requests
      if (universalData.special_requests) {
        details.push({ label: 'Special Requests', value: universalData.special_requests });
      }
      
      // Children Information (if available)
      if (universalData.children && universalData.children.length > 0) {
        const childrenInfo = universalData.children.map(child => `Age ${child.age}`).join(', ');
        details.push({ label: 'Children', value: childrenInfo });
      }
    }

    // Universal Studios Hollywood Package Details - Comprehensive display of all form fields
    if (component.type === 'universal-hollywood-package') {
      const hollywoodData = component.data || {};
      
      // Hotel Accommodation Details
      if (hollywoodData.hotel_name) {
        details.push({ label: 'Hotel', value: hollywoodData.hotel_name });
      }
      if (hollywoodData.room_type) {
        details.push({ label: 'Room Type', value: hollywoodData.room_type });
      }
      if (hollywoodData.view) {
        details.push({ label: 'View', value: hollywoodData.view });
      }
      if (hollywoodData.check_in_date && hollywoodData.check_out_date) {
        details.push({ 
          label: 'Stay Dates', 
          value: `${formatTravelDate(hollywoodData.check_in_date)} to ${formatTravelDate(hollywoodData.check_out_date)}` 
        });
      } else if (hollywoodData.check_in_date) {
        details.push({ label: 'Check-in', value: formatTravelDate(hollywoodData.check_in_date) });
      } else if (hollywoodData.check_out_date) {
        details.push({ label: 'Check-out', value: formatTravelDate(hollywoodData.check_out_date) });
      }
      
      // Theme Park Tickets
      if (hollywoodData.ticket_type) {
        details.push({ label: 'Ticket Type', value: hollywoodData.ticket_type });
      }
      if (hollywoodData.express_pass && hollywoodData.express_pass !== 'None') {
        details.push({ label: 'Express Pass', value: hollywoodData.express_pass });
      }
      
      // Studio Experiences
      if (hollywoodData.vip_experience && hollywoodData.vip_experience !== 'None') {
        details.push({ label: 'VIP Experience', value: hollywoodData.vip_experience });
      }
      if (hollywoodData.studio_tour) {
        details.push({ label: 'Studio Tour', value: hollywoodData.studio_tour });
      }
      if (hollywoodData.behind_scenes_tours) {
        details.push({ label: 'Behind-the-Scenes Tours', value: hollywoodData.behind_scenes_tours });
      }
      
      // Add-on Services
      if (hollywoodData.ground_transportation) {
        details.push({ label: 'Ground Transportation', value: hollywoodData.ground_transportation });
      }
      if (hollywoodData.travel_protection && hollywoodData.travel_protection === 'Included') {
        const protectionText = hollywoodData.travel_protection_price ? 
          `Included ($${parseFloat(hollywoodData.travel_protection_price).toFixed(2)})` : 
          'Included';
        details.push({ label: 'Travel Protection', value: protectionText });
      }
      
      // Pricing & Payment Information
      if (hollywoodData.total_price_due_by) {
        details.push({ label: 'Total Due By', value: formatTravelDate(hollywoodData.total_price_due_by) });
      }
      if (hollywoodData.deposit_due && hollywoodData.deposit_due > 0) {
        const depositText = hollywoodData.deposit_due_by ? 
          `$${parseFloat(hollywoodData.deposit_due).toFixed(2)} (Due: ${formatTravelDate(hollywoodData.deposit_due_by)})` : 
          `$${parseFloat(hollywoodData.deposit_due).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }
      
      // Special Requests
      if (hollywoodData.special_requests) {
        details.push({ label: 'Special Requests', value: hollywoodData.special_requests });
      }
      
      // Children Information (if available)
      if (hollywoodData.children && hollywoodData.children.length > 0) {
        const childrenInfo = hollywoodData.children.map(child => `Age ${child.age}`).join(', ');
        details.push({ label: 'Children', value: childrenInfo });
      }
    }

    // Disney Cruise Line Package Details - Comprehensive display of all form fields
    if (component.type === 'disney-cruise-line') {
      const cruiseData = component.data || {};
      
      // Cruise Details
      if (cruiseData.ship_name) {
        details.push({ label: 'Disney Ship', value: cruiseData.ship_name });
      }
      if (cruiseData.cruise_length) {
        details.push({ label: 'Cruise Length', value: cruiseData.cruise_length });
      }
      if (cruiseData.departure_date && cruiseData.return_date) {
        details.push({ 
          label: 'Cruise Dates', 
          value: `${formatTravelDate(cruiseData.departure_date)} to ${formatTravelDate(cruiseData.return_date)}` 
        });
      } else if (cruiseData.departure_date) {
        details.push({ label: 'Departure Date', value: formatTravelDate(cruiseData.departure_date) });
      } else if (cruiseData.return_date) {
        details.push({ label: 'Return Date', value: formatTravelDate(cruiseData.return_date) });
      }
      if (cruiseData.departure_port) {
        const departurePortValue = cruiseData.departure_port === 'Other (Please Specify)' 
          ? cruiseData.departure_port_other || cruiseData.departure_port
          : cruiseData.departure_port;
        details.push({ label: 'Departure Port', value: departurePortValue });
      }
      if (cruiseData.destination) {
        details.push({ label: 'Destination', value: cruiseData.destination });
      }
      if (cruiseData.itinerary) {
        details.push({ label: 'Itinerary', value: cruiseData.itinerary });
      }
      
      // Stateroom Details
      if (cruiseData.stateroom_category) {
        details.push({ label: 'Stateroom Category', value: cruiseData.stateroom_category });
      }
      if (cruiseData.stateroom_number) {
        details.push({ label: 'Stateroom Number', value: cruiseData.stateroom_number });
      }
      if (cruiseData.deck) {
        details.push({ label: 'Deck', value: cruiseData.deck });
      }
      if (cruiseData.stateroom_location) {
        details.push({ label: 'Stateroom Location', value: cruiseData.stateroom_location });
      }
      
      // Dining & Experiences
      if (cruiseData.dining_rotation) {
        details.push({ label: 'Dining Rotation', value: cruiseData.dining_rotation });
      }
      if (cruiseData.dining_time) {
        details.push({ label: 'Main Dining Time', value: cruiseData.dining_time });
      }
      if (cruiseData.port_adventures) {
        const portAdventuresText = cruiseData.port_adventures_price && cruiseData.port_adventures_price > 0 ? 
          `${cruiseData.port_adventures} ($${parseFloat(cruiseData.port_adventures_price).toFixed(2)})` : 
          cruiseData.port_adventures;
        details.push({ label: 'Port Adventures', value: portAdventuresText });
      }
      if (cruiseData.onboard_activities) {
        details.push({ label: 'Onboard Activities', value: cruiseData.onboard_activities });
      }
      if (cruiseData.character_experiences) {
        details.push({ label: 'Character Experiences', value: cruiseData.character_experiences });
      }
      if (cruiseData.adult_exclusive_areas) {
        details.push({ label: 'Adult Exclusive Areas', value: cruiseData.adult_exclusive_areas });
      }
      if (cruiseData.kids_clubs) {
        details.push({ label: 'Kids Clubs', value: cruiseData.kids_clubs });
      }
      
      // Premium Services
      if (cruiseData.spa_services) {
        const spaServicesText = cruiseData.spa_services_price && cruiseData.spa_services_price > 0 ? 
          `${cruiseData.spa_services} ($${parseFloat(cruiseData.spa_services_price).toFixed(2)})` : 
          cruiseData.spa_services;
        details.push({ label: 'Spa Services', value: spaServicesText });
      }
      if (cruiseData.specialty_dining) {
        const specialtyDiningText = cruiseData.specialty_dining_price && cruiseData.specialty_dining_price > 0 ? 
          `${cruiseData.specialty_dining} ($${parseFloat(cruiseData.specialty_dining_price).toFixed(2)})` : 
          cruiseData.specialty_dining;
        details.push({ label: 'Specialty Dining', value: specialtyDiningText });
      }
      
      // Add-On Packages
      if (cruiseData.beverage_package && cruiseData.beverage_package !== 'Not Included') {
        details.push({ label: 'Beverage Package', value: cruiseData.beverage_package });
      }
      if (cruiseData.wifi_package && cruiseData.wifi_package !== 'Not Included') {
        const wifiPackageText = cruiseData.wifi_package_price && cruiseData.wifi_package_price > 0 ? 
          `${cruiseData.wifi_package} ($${parseFloat(cruiseData.wifi_package_price).toFixed(2)})` : 
          cruiseData.wifi_package;
        details.push({ label: 'WiFi Package', value: wifiPackageText });
      }
      if (cruiseData.photo_package && cruiseData.photo_package !== 'Not Included') {
        const photoPackageText = cruiseData.photo_package_price && cruiseData.photo_package_price > 0 ? 
          `${cruiseData.photo_package} ($${parseFloat(cruiseData.photo_package_price).toFixed(2)})` : 
          cruiseData.photo_package;
        details.push({ label: 'Photo Package', value: photoPackageText });
      }
      if (cruiseData.gratuities && cruiseData.gratuities !== 'Not Included') {
        const gratuitiesText = cruiseData.gratuities_price && cruiseData.gratuities_price > 0 ? 
          `${cruiseData.gratuities} ($${parseFloat(cruiseData.gratuities_price).toFixed(2)})` : 
          cruiseData.gratuities;
        details.push({ label: 'Gratuities', value: gratuitiesText });
      }
      if (cruiseData.travel_protection && cruiseData.travel_protection === 'Included') {
        const protectionText = cruiseData.travel_protection_price ? 
          `Included ($${parseFloat(cruiseData.travel_protection_price).toFixed(2)})` : 
          'Included';
        details.push({ label: 'Travel Protection', value: protectionText });
      }
      
      // Pricing & Payment Information
      if (cruiseData.total_price_due_by) {
        details.push({ label: 'Total Due By', value: formatTravelDate(cruiseData.total_price_due_by) });
      }
      if (cruiseData.deposit_due && cruiseData.deposit_due > 0) {
        const depositText = cruiseData.deposit_due_by ? 
          `$${parseFloat(cruiseData.deposit_due).toFixed(2)} (Due: ${formatTravelDate(cruiseData.deposit_due_by)})` : 
          `$${parseFloat(cruiseData.deposit_due).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }
      if (cruiseData.final_payment_due) {
        details.push({ label: 'Final Payment Due', value: formatTravelDate(cruiseData.final_payment_due) });
      }
      
      // Special Information
      if (cruiseData.special_requests) {
        details.push({ label: 'Special Requests', value: cruiseData.special_requests });
      }
      if (cruiseData.celebration_details) {
        details.push({ label: 'Celebration Details', value: cruiseData.celebration_details });
      }
      if (cruiseData.accessibility_needs) {
        details.push({ label: 'Accessibility Needs', value: cruiseData.accessibility_needs });
      }
      
      // Children Information (if available)
      if (cruiseData.children && cruiseData.children.length > 0) {
        const childrenInfo = cruiseData.children.map(child => `Age ${child.age}`).join(', ');
        details.push({ label: 'Children', value: childrenInfo });
      }
    }

    // Royal Caribbean Cruise Package Details - Comprehensive display of all form fields
    if (component.type === 'royal-caribbean-cruise') {
      const cruiseData = component.data || {};
      
      // Cruise Details
      if (cruiseData.ship_name) {
        details.push({ label: 'Royal Caribbean Ship', value: cruiseData.ship_name });
      }
      if (cruiseData.cruise_length) {
        details.push({ label: 'Cruise Length', value: cruiseData.cruise_length });
      }
      if (cruiseData.departure_date && cruiseData.return_date) {
        details.push({ 
          label: 'Cruise Dates', 
          value: `${formatTravelDate(cruiseData.departure_date)} to ${formatTravelDate(cruiseData.return_date)}` 
        });
      } else if (cruiseData.departure_date) {
        details.push({ label: 'Departure Date', value: formatTravelDate(cruiseData.departure_date) });
      } else if (cruiseData.return_date) {
        details.push({ label: 'Return Date', value: formatTravelDate(cruiseData.return_date) });
      }
      if (cruiseData.departure_port) {
        const departurePortValue = cruiseData.departure_port === 'Other (Please Specify)' 
          ? cruiseData.departure_port_other || cruiseData.departure_port
          : cruiseData.departure_port;
        details.push({ label: 'Departure Port', value: departurePortValue });
      }
      if (cruiseData.destination) {
        details.push({ label: 'Destination', value: cruiseData.destination });
      }
      if (cruiseData.itinerary) {
        details.push({ label: 'Itinerary', value: cruiseData.itinerary });
      }
      
      // Stateroom Details
      if (cruiseData.stateroom_category) {
        details.push({ label: 'Stateroom Category', value: cruiseData.stateroom_category });
      }
      if (cruiseData.stateroom_number) {
        details.push({ label: 'Stateroom Number', value: cruiseData.stateroom_number });
      }
      if (cruiseData.deck) {
        details.push({ label: 'Deck', value: cruiseData.deck });
      }
      if (cruiseData.stateroom_location) {
        details.push({ label: 'Stateroom Location', value: cruiseData.stateroom_location });
      }
      
      // Dining & Entertainment
      if (cruiseData.dining_package) {
        details.push({ label: 'Dining Package', value: cruiseData.dining_package });
      }
      if (cruiseData.dining_time) {
        details.push({ label: 'Main Dining Time', value: cruiseData.dining_time });
      }
      if (cruiseData.shore_excursions) {
        const shoreExcursionsText = cruiseData.shore_excursions_price && cruiseData.shore_excursions_price > 0 ? 
          `${cruiseData.shore_excursions} ($${parseFloat(cruiseData.shore_excursions_price).toFixed(2)})` : 
          cruiseData.shore_excursions;
        details.push({ label: 'Shore Excursions', value: shoreExcursionsText });
      }
      if (cruiseData.onboard_activities) {
        details.push({ label: 'Onboard Activities', value: cruiseData.onboard_activities });
      }
      if (cruiseData.entertainment) {
        details.push({ label: 'Entertainment', value: cruiseData.entertainment });
      }
      
      // Specialty Services
      if (cruiseData.specialty_restaurants) {
        const specialtyDiningText = cruiseData.specialty_dining_price && cruiseData.specialty_dining_price > 0 ? 
          `${cruiseData.specialty_restaurants} ($${parseFloat(cruiseData.specialty_dining_price).toFixed(2)})` : 
          cruiseData.specialty_restaurants;
        details.push({ label: 'Specialty Restaurants', value: specialtyDiningText });
      }
      if (cruiseData.spa_services) {
        const spaServicesText = cruiseData.spa_services_price && cruiseData.spa_services_price > 0 ? 
          `${cruiseData.spa_services} ($${parseFloat(cruiseData.spa_services_price).toFixed(2)})` : 
          cruiseData.spa_services;
        details.push({ label: 'Spa Services', value: spaServicesText });
      }
      
      // Add-On Packages
      if (cruiseData.beverage_package && cruiseData.beverage_package !== 'Not Included') {
        const beveragePackageText = cruiseData.beverage_package_price && cruiseData.beverage_package_price > 0 ? 
          `${cruiseData.beverage_package} ($${parseFloat(cruiseData.beverage_package_price).toFixed(2)})` : 
          cruiseData.beverage_package;
        details.push({ label: 'Beverage Package', value: beveragePackageText });
      }
      if (cruiseData.internet_package && cruiseData.internet_package !== 'Not Included') {
        const internetPackageText = cruiseData.internet_package_price && cruiseData.internet_package_price > 0 ? 
          `${cruiseData.internet_package} ($${parseFloat(cruiseData.internet_package_price).toFixed(2)})` : 
          cruiseData.internet_package;
        details.push({ label: 'Internet Package', value: internetPackageText });
      }
      if (cruiseData.photo_package && cruiseData.photo_package !== 'Not Included') {
        const photoPackageText = cruiseData.photo_package_price && cruiseData.photo_package_price > 0 ? 
          `${cruiseData.photo_package} ($${parseFloat(cruiseData.photo_package_price).toFixed(2)})` : 
          cruiseData.photo_package;
        details.push({ label: 'Photo Package', value: photoPackageText });
      }
      if (cruiseData.gratuities && cruiseData.gratuities !== 'Not Included') {
        const gratuitiesText = cruiseData.gratuities_price && cruiseData.gratuities_price > 0 ? 
          `${cruiseData.gratuities} ($${parseFloat(cruiseData.gratuities_price).toFixed(2)})` : 
          cruiseData.gratuities;
        details.push({ label: 'Gratuities', value: gratuitiesText });
      }
      if (cruiseData.travel_protection && cruiseData.travel_protection === 'Included') {
        const protectionText = cruiseData.travel_protection_price ? 
          `Included ($${parseFloat(cruiseData.travel_protection_price).toFixed(2)})` : 
          'Included';
        details.push({ label: 'Travel Protection', value: protectionText });
      }
      
      // Pricing & Payment Information
      if (cruiseData.total_price_due_by) {
        details.push({ label: 'Total Due By', value: formatTravelDate(cruiseData.total_price_due_by) });
      }
      if (cruiseData.deposit_due && cruiseData.deposit_due > 0) {
        const depositText = cruiseData.deposit_due_by ? 
          `$${parseFloat(cruiseData.deposit_due).toFixed(2)} (Due: ${formatTravelDate(cruiseData.deposit_due_by)})` : 
          `$${parseFloat(cruiseData.deposit_due).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }
      if (cruiseData.final_payment_due) {
        details.push({ label: 'Final Payment Due', value: formatTravelDate(cruiseData.final_payment_due) });
      }
      
      // Special Information
      if (cruiseData.special_requests) {
        details.push({ label: 'Special Requests', value: cruiseData.special_requests });
      }
      if (cruiseData.celebration_details) {
        details.push({ label: 'Celebration Details', value: cruiseData.celebration_details });
      }
      if (cruiseData.accessibility_needs) {
        details.push({ label: 'Accessibility Needs', value: cruiseData.accessibility_needs });
      }
      
      // Children Information (if available)
      if (cruiseData.children && cruiseData.children.length > 0) {
        const childrenInfo = cruiseData.children.map(child => `Age ${child.age}`).join(', ');
        details.push({ label: 'Children', value: childrenInfo });
      }
    }

    // Carnival Cruise Package Details - Comprehensive display of all form fields
    if (component.type === 'carnival-cruise') {
      const cruiseData = component.data || {};
      
      // Cruise Details
      if (cruiseData.ship_name) {
        details.push({ label: 'Carnival Ship', value: cruiseData.ship_name });
      }
      if (cruiseData.cruise_length) {
        details.push({ label: 'Cruise Length', value: cruiseData.cruise_length });
      }
      if (cruiseData.departure_date && cruiseData.return_date) {
        details.push({ 
          label: 'Cruise Dates', 
          value: `${formatTravelDate(cruiseData.departure_date)} to ${formatTravelDate(cruiseData.return_date)}` 
        });
      } else if (cruiseData.departure_date) {
        details.push({ label: 'Departure Date', value: formatTravelDate(cruiseData.departure_date) });
      } else if (cruiseData.return_date) {
        details.push({ label: 'Return Date', value: formatTravelDate(cruiseData.return_date) });
      }
      if (cruiseData.departure_port) {
        const departurePortValue = cruiseData.departure_port === 'Other (Please Specify)' 
          ? cruiseData.departure_port_other || cruiseData.departure_port
          : cruiseData.departure_port;
        details.push({ label: 'Departure Port', value: departurePortValue });
      }
      if (cruiseData.destination) {
        details.push({ label: 'Destination', value: cruiseData.destination });
      }
      if (cruiseData.itinerary) {
        details.push({ label: 'Itinerary', value: cruiseData.itinerary });
      }
      
      // Stateroom Details
      if (cruiseData.stateroom_category) {
        details.push({ label: 'Stateroom Category', value: cruiseData.stateroom_category });
      }
      if (cruiseData.stateroom_number) {
        details.push({ label: 'Stateroom Number', value: cruiseData.stateroom_number });
      }
      if (cruiseData.deck) {
        details.push({ label: 'Deck', value: cruiseData.deck });
      }
      if (cruiseData.stateroom_location) {
        details.push({ label: 'Stateroom Location', value: cruiseData.stateroom_location });
      }
      
      // Dining & Entertainment
      if (cruiseData.dining_package) {
        details.push({ label: 'Dining Package', value: cruiseData.dining_package });
      }
      if (cruiseData.dining_time) {
        details.push({ label: 'Main Dining Time', value: cruiseData.dining_time });
      }
      if (cruiseData.shore_excursions) {
        const shoreExcursionsText = cruiseData.shore_excursions_price && cruiseData.shore_excursions_price > 0 ? 
          `${cruiseData.shore_excursions} ($${parseFloat(cruiseData.shore_excursions_price).toFixed(2)})` : 
          cruiseData.shore_excursions;
        details.push({ label: 'Shore Excursions', value: shoreExcursionsText });
      }
      if (cruiseData.onboard_activities) {
        details.push({ label: 'Onboard Activities', value: cruiseData.onboard_activities });
      }
      if (cruiseData.entertainment) {
        details.push({ label: 'Entertainment', value: cruiseData.entertainment });
      }
      
      // Specialty Services
      if (cruiseData.specialty_restaurants) {
        const specialtyDiningText = cruiseData.specialty_dining_price && cruiseData.specialty_dining_price > 0 ? 
          `${cruiseData.specialty_restaurants} ($${parseFloat(cruiseData.specialty_dining_price).toFixed(2)})` : 
          cruiseData.specialty_restaurants;
        details.push({ label: 'Specialty Restaurants', value: specialtyDiningText });
      }
      if (cruiseData.spa_services) {
        const spaServicesText = cruiseData.spa_services_price && cruiseData.spa_services_price > 0 ? 
          `${cruiseData.spa_services} ($${parseFloat(cruiseData.spa_services_price).toFixed(2)})` : 
          cruiseData.spa_services;
        details.push({ label: 'Spa Services', value: spaServicesText });
      }
      
      // Add-On Packages
      if (cruiseData.beverage_package && cruiseData.beverage_package !== 'Not Included') {
        const beveragePackageText = cruiseData.beverage_package_price && cruiseData.beverage_package_price > 0 ? 
          `${cruiseData.beverage_package} ($${parseFloat(cruiseData.beverage_package_price).toFixed(2)})` : 
          cruiseData.beverage_package;
        details.push({ label: 'Beverage Package', value: beveragePackageText });
      }
      if (cruiseData.internet_package && cruiseData.internet_package !== 'Not Included') {
        const internetPackageText = cruiseData.internet_package_price && cruiseData.internet_package_price > 0 ? 
          `${cruiseData.internet_package} ($${parseFloat(cruiseData.internet_package_price).toFixed(2)})` : 
          cruiseData.internet_package;
        details.push({ label: 'Internet Package', value: internetPackageText });
      }
      if (cruiseData.photo_package && cruiseData.photo_package !== 'Not Included') {
        const photoPackageText = cruiseData.photo_package_price && cruiseData.photo_package_price > 0 ? 
          `${cruiseData.photo_package} ($${parseFloat(cruiseData.photo_package_price).toFixed(2)})` : 
          cruiseData.photo_package;
        details.push({ label: 'Photo Package', value: photoPackageText });
      }
      if (cruiseData.gratuities && cruiseData.gratuities !== 'Not Included') {
        const gratuitiesText = cruiseData.gratuities_price && cruiseData.gratuities_price > 0 ? 
          `${cruiseData.gratuities} ($${parseFloat(cruiseData.gratuities_price).toFixed(2)})` : 
          cruiseData.gratuities;
        details.push({ label: 'Gratuities', value: gratuitiesText });
      }
      if (cruiseData.travel_protection && cruiseData.travel_protection === 'Included') {
        const protectionText = cruiseData.travel_protection_price ? 
          `Included ($${parseFloat(cruiseData.travel_protection_price).toFixed(2)})` : 
          'Included';
        details.push({ label: 'Travel Protection', value: protectionText });
      }
      
      // Pricing & Payment Information
      if (cruiseData.total_price_due_by) {
        details.push({ label: 'Total Due By', value: formatTravelDate(cruiseData.total_price_due_by) });
      }
      if (cruiseData.deposit_due && cruiseData.deposit_due > 0) {
        const depositText = cruiseData.deposit_due_by ? 
          `$${parseFloat(cruiseData.deposit_due).toFixed(2)} (Due: ${formatTravelDate(cruiseData.deposit_due_by)})` : 
          `$${parseFloat(cruiseData.deposit_due).toFixed(2)}`;
        details.push({ label: 'Deposit Required', value: depositText });
      }
      if (cruiseData.final_payment_due) {
        details.push({ label: 'Final Payment Due', value: formatTravelDate(cruiseData.final_payment_due) });
      }
      
      // Special Information
      if (cruiseData.special_requests) {
        details.push({ label: 'Special Requests', value: cruiseData.special_requests });
      }
      if (cruiseData.celebration_details) {
        details.push({ label: 'Celebration Details', value: cruiseData.celebration_details });
      }
      if (cruiseData.accessibility_needs) {
        details.push({ label: 'Accessibility Needs', value: cruiseData.accessibility_needs });
      }
      
      // Children Information (if available)
      if (cruiseData.children && cruiseData.children.length > 0) {
        const childrenInfo = cruiseData.children.map(child => `Age ${child.age}`).join(', ');
        details.push({ label: 'Children', value: childrenInfo });
      }
    }

    // General Details
    if (component.address) {
      details.push({ label: 'Address', value: component.address });
    }
    if (component.agencyName) {
      details.push({ label: 'Booking Agency', value: component.agencyName });
    }
    if (component.specialRequests) {
      details.push({ label: 'Special Requests', value: component.specialRequests });
    }
    if (component.notes) {
      details.push({ label: 'Notes', value: component.notes });
    }

    // Deposit Information (for components not already handled above)
    if (component.depositRequired && component.depositAmount && component.type !== 'package') {
      const depositText = `$${parseFloat(component.depositAmount || 0).toFixed(2)}${component.depositDueDate ? ` (Due: ${formatTravelDate(component.depositDueDate)})` : ''}`;
      details.push({ label: 'Deposit Required', value: depositText });
    }

    return details;
  };

  if (loading) {
    return (
      <div className="modern-quote-loading">
        <div className="loading-spinner"></div>
        <p>Loading your travel quote...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modern-quote-error">
        <h2>Quote Not Available</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="modern-quote-error">
        <h2>Quote Not Found</h2>
        <p>The requested quote could not be found.</p>
      </div>
    );
  }

  const leadTraveler = quote.lead_traveler || {};
  const agencyInfo = quote.agency_info || {};
  
  // Calculate effective deposit amount - prioritize component deposits over quote-level deposit
  const calculateEffectiveDepositAmount = (quote) => {
    // First, try to calculate from components
    let componentDeposits = 0;
    if (quote.components && Array.isArray(quote.components)) {
      quote.components.forEach(component => {
        // Check component data for deposit_due (Disney components, etc.)
        if (component.data && component.data.deposit_due) {
          componentDeposits += parseFloat(component.data.deposit_due) || 0;
        }
        // Check top-level component deposit settings
        else if (component.depositRequired === true && component.depositAmount) {
          componentDeposits += parseFloat(component.depositAmount) || 0;
        }
      });
    }
    
    if (componentDeposits > 0) {
      return componentDeposits;
    }
    
    // Fallback to quote-level deposit amount
    const quoteDeposit = parseFloat(quote.deposit_amount) || 0;
    if (quoteDeposit > 0) {
      return quoteDeposit;
    }
    
    // Final fallback to total amount
    return parseFloat(quote.total_amount) || 0;
  };

  // Calculate display amounts based on client selections
  const calculateDisplayAmounts = (quote, clientSelections) => {
    const components = quote.components || [];
    const hasSelections = Object.keys(clientSelections).length > 0;
    
    if (!hasSelections) {
      // No selections - show original amounts
      return {
        totalAmount: parseFloat(quote.total_amount) || 0,
        depositAmount: calculateEffectiveDepositAmount(quote)
      };
    }
    
    // Calculate based on selections
    let selectedTotal = 0;
    let selectedDeposit = 0;
    
    components.forEach(component => {
      const isOption = component.isOption || component.data?.isOption || false;
      const isSelected = clientSelections[component.id];
      const shouldInclude = !isOption || isSelected;
      
      if (shouldInclude) {
        const componentPrice = parseFloat(component.price) || 0;
        const paymentTiming = component.paymentTiming || component.data?.paymentTiming;
        
        // Handle optional components with payment timing
        if (isOption && paymentTiming) {
          if (paymentTiming === 'immediate') {
            // Pay full price when selected - add to both total and deposit
            selectedTotal += componentPrice;
            selectedDeposit += componentPrice;
          } else if (paymentTiming === 'with_deposit') {
            // Pay deposit when selected - add full price to total, deposit to deposit
            selectedTotal += componentPrice;
            const depositAmt = parseFloat(component.optionalDepositAmount || component.data?.optionalDepositAmount) || 0;
            selectedDeposit += depositAmt;
          } else if (paymentTiming === 'with_final') {
            // Pay with final payment - add to total but not deposit
            selectedTotal += componentPrice;
          } else if (paymentTiming === 'bundled') {
            // Already included - don't add to total or deposit
          }
        } else {
          // Required component or optional without payment timing
          selectedTotal += componentPrice;
          
          // Calculate deposit for required components
          if (component.depositRequired && component.depositAmount) {
            selectedDeposit += parseFloat(component.depositAmount) || 0;
          } else if (component.data && component.data.deposit_due) {
            selectedDeposit += parseFloat(component.data.deposit_due) || 0;
          } else {
            // No deposit specified, use full component price
            selectedDeposit += componentPrice;
          }
        }
      }
    });
    
    return {
      totalAmount: selectedTotal,
      depositAmount: selectedDeposit
    };
  };
  
  // Get current display amounts based on selections
  const displayAmounts = calculateDisplayAmounts(quote, clientSelections);
  const depositAmount = displayAmounts.depositAmount;
  
  // Debug logging for payment calculation
  console.log('💰 Payment Calculation Debug:', {
    hasSelections: Object.keys(clientSelections).length > 0,
    selectedComponents: Object.keys(clientSelections).filter(key => clientSelections[key]),
    originalTotal: parseFloat(quote.total_amount) || 0,
    calculatedTotal: displayAmounts.totalAmount,
    originalDeposit: calculateEffectiveDepositAmount(quote),
    calculatedDeposit: displayAmounts.depositAmount,
    clientSelections: clientSelections
  });
  
  // Ensure components is always an array
  let components = quote.components || [];
  if (!Array.isArray(components)) {
    console.warn('Components is not an array:', typeof components, components);
    components = [];
  }

  // If quote has a template_id, use the new QuoteRenderer system
  if (quote.template_id && quote.template_id !== 'modern') {
    return (
      <div className="modern-quote-container">
        {/* Agent Preview Header */}
        {isAgentPreview && (
          <div className="agent-preview-header">
            <button 
              onClick={() => navigate('/agent/quotes')}
              className="back-button"
            >
              ← Back to Quotes
            </button>
            <div className="preview-badge">
              <span>Agent Preview Mode</span>
            </div>
          </div>
        )}
        
        {/* Use QuoteRenderer for template-based quotes */}
        <QuoteRenderer 
          quoteData={{
            id: quote.id,
            quoteNumber: quote.quote_number,
            quoteTitle: quote.title,
            client: {
              firstName: quote.client_name?.split(' ')[0] || '',
              lastName: quote.client_name?.split(' ').slice(1).join(' ') || '',
              email: quote.client_email || '',
              phone: quote.client_phone || ''
            },
            travelDates: {
              start: quote.travel_start_date,
              end: quote.travel_end_date
            },
            travelers: {
              adults: quote.adults || 1,
              children: Array(quote.children || 0).fill({})
            },
            components: quote.components || [],
            totalPrice: parseFloat(quote.total_amount) || 0,
            depositAmount: parseFloat(quote.deposit_amount) || 0,
            depositDueDate: quote.deposit_due_date,
            notes: quote.notes || '',
            agent: quote.agent || {},
            agent_name: quote.agent_name || '',
            agent_email: quote.agent_email || '',
            agent_phone: quote.agent_phone || '',
            agent_business_name: quote.agent_business_name || quote.business_name || '',
            agent_logo_url: quote.agent_logo_url || quote.logo_url || ''
          }}
          templateId={quote.template_id}
          onPayment={handlePayDeposit}
          isAgentPreview={isAgentPreview}
        />
      </div>
    );
  }

  return (
    <div className="modern-quote-container">
      {/* Agent Preview Header */}
      {isAgentPreview && (
        <div className="agent-preview-header">
          <button 
            onClick={() => navigate('/agent/quotes')}
            className="back-button"
          >
            ← Back to Quotes
          </button>
          <div className="preview-badge">
            <span>Agent Preview Mode</span>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="modern-quote-header">
        <h1>✈️ Your Dream Trip Awaits</h1>
        <div className="quote-number">Quote #{quote.quote_number}</div>
      </div>

      <div className="modern-quote-content">
        {/* Client Information */}
        <div className="client-info">
          <h2>👥 Trip Details</h2>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Traveler</div>
              <div className="info-value">
                {leadTraveler.firstName && leadTraveler.lastName 
                  ? `${leadTraveler.firstName} ${leadTraveler.lastName}`
                  : quote.client_name || 'Valued Client'
                }
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Travel Dates</div>
              <div className="info-value">
                {formatDateRange(quote.travel_start_date, quote.travel_end_date) || 'To be determined'}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Duration</div>
              <div className="info-value">
                {calculateDuration(quote.travel_start_date, quote.travel_end_date) || 'To be determined'}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Destination</div>
              <div className="info-value">{quote.title || 'Custom Travel Package'}</div>
            </div>
          </div>
        </div>

        {/* Itinerary Components */}
        <div className="itinerary-section">
          <h2 className="section-title">🗺️ Your Itinerary</h2>

          {/* Optional Components Guidance */}
          {(() => {
            const optionalComponents = components.filter(component => 
              component.isOption || component.data?.isOption || false
            );
            const hasOptionalComponents = optionalComponents.length > 0;
            
            if (hasOptionalComponents) {
              return (
                <div className="options-guidance">
                  <div className="guidance-header">
                    <div className="guidance-icon">🎯</div>
                    <div className="guidance-content">
                      <h3 className="guidance-title">Customize Your Trip</h3>
                      <p className="guidance-description">
                        This quote includes optional components that you can add or remove to personalize your travel experience.
                      </p>
                    </div>
                  </div>
                  
                  <div className="how-to-select">
                    <h4 className="instructions-title">How to Select Options:</h4>
                    <ol className="instructions-list">
                      <li>Review the optional components below (marked with <span className="highlight-badge">Optional</span> badges)</li>
                      <li>Check the boxes next to options you want to include in your trip</li>
                      <li>Watch your updated total in the pricing summary section</li>
                      <li>Proceed to payment when you're satisfied with your selections</li>
                    </ol>
                  </div>
                  
                  <div className="options-summary">
                    <div className="summary-stats">
                      <span className="stat-item">
                        <strong>{optionalComponents.length}</strong> optional component{optionalComponents.length !== 1 ? 's' : ''} available
                      </span>
                      <span className="stat-divider">•</span>
                      <span className="stat-item">
                        Customize to match your preferences and budget
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {components.length > 0 ? (
            components
              .filter(component => component && component.title && component.title.trim() !== '')
              .map((component, index) => {
                const typeInfo = getComponentTypeInfo(component.type);
                const details = getComponentDetails(component);

                const isOption = component.isOption || component.data?.isOption || false;
                const optionDescription = component.optionDescription || component.data?.optionDescription || '';
                const isSelected = clientSelections[component.id];
                const selectionClass = isOption ? (isSelected ? 'selected' : 'not-selected') : '';
                
                return (
                  <div key={component.id || index} className={`component-card ${isOption ? 'optional-component' : 'required-component'} ${selectionClass}`}>
                    <div className="component-header">
                      <div className="component-title">
                        {isOption && (
                          <input
                            type="checkbox"
                            className="component-checkbox"
                            checked={clientSelections[component.id] || false}
                            onChange={(e) => handleComponentSelection(component.id, e.target.checked)}
                            disabled={isAgentPreview}
                            title={isAgentPreview ? "Read-only in agent preview mode" : ""}
                          />
                        )}
                        <div className="component-icon">{typeInfo.icon}</div>
                        <div className="component-title-text">
                          {component.title}
                          {isOption && (
                            <span className="optional-badge">Optional</span>
                          )}
                        </div>
                      </div>
                      <div className="component-price">{formatPrice(component.price)}</div>
                    </div>
                    <div className="component-details">
                      {details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="detail-row">
                          <div className="detail-label">{detail.label}</div>
                          <div className="detail-value">{detail.value}</div>
                        </div>
                      ))}
                      {isOption && optionDescription && (
                        <div className="option-description">
                          <strong>About this option:</strong> {optionDescription}
                        </div>
                      )}
                    </div>
                    
                    {/* Component Attachments */}
                    {((component.photos && component.photos.length > 0) || (component.documents && component.documents.length > 0)) && (
                      <div className="component-attachments">
                        {component.photos && component.photos.length > 0 && (
                          <div className="component-photos">
                            <h4>📸 Photos</h4>
                            <div className="photos-grid">
                              {component.photos.map((photo, photoIndex) => (
                                <img 
                                  key={photoIndex}
                                  src={photo.url} 
                                  alt={photo.name || 'Component photo'} 
                                  className="component-photo"
                                  onClick={() => window.open(photo.url, '_blank')}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {component.documents && component.documents.length > 0 && (
                          <div className="component-documents">
                            <h4>📋 Documents</h4>
                            <div className="documents-list">
                              {component.documents.map((doc, docIndex) => (
                                <a 
                                  key={docIndex}
                                  href={doc.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="document-link"
                                >
                                  📄 {doc.name}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
          ) : (
            <div className="no-components">
              <p>Itinerary details will be added by your travel agent.</p>
            </div>
          )}
        </div>

        {/* Pricing Summary */}
        <div className="pricing-summary">
          <h2 className="pricing-title">💰 Quote Summary</h2>
          <div className="pricing-breakdown">
            {components.length > 0 && components
              .filter(component => component.title && component.price > 0)
              .map((component, index) => {
                const isSelected = clientSelections[component.id];
                const isOption = component.isOption || component.data?.isOption || false;
                const showComponent = !isOption || isSelected;
                
                return showComponent ? (
                  <div key={index} className={`pricing-row ${isOption ? 'optional-item' : 'required-item'}`}>
                    <span>
                      {component.title}
                      {isOption && <span className="optional-indicator"> (Optional)</span>}
                    </span>
                    <span>{formatPrice(component.price)}</span>
                  </div>
                ) : null;
              })}
            
            {(() => {
              const totals = calculateSelectedTotals();
              return (
                <>
                  <div className="pricing-row total-row">
                    <span><strong>Total:</strong></span>
                    <span><strong>{formatPrice(totals.total)}</strong></span>
                  </div>
                  <div className="pricing-row deposit-row">
                    <span><strong>Initial Payment Due:</strong></span>
                    <span><strong>{formatPrice(totals.deposit)}</strong></span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Additional Notes */}
        {quote.notes && (
          <div className="quote-notes">
            <h3>Additional Information</h3>
            <p>{quote.notes}</p>
          </div>
        )}

        {/* Attachments */}
        {quote.attachments && quote.attachments.length > 0 && (
          <div className="quote-attachments">
            <h3>Attachments</h3>
            <div className="attachments-list">
              {quote.attachments.map((file) => (
                <div key={file.id} className="attachment-item">
                  <span className="file-icon">📎</span>
                  <span className="file-name">{file.file_name}</span>
                  <button 
                    className="download-btn"
                    onClick={() => downloadAttachment(file.id, file.file_name)}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="cta-section">
          <h3>Ready to Make This Trip a Reality?</h3>
          <p>Submit Your Payment Information Below</p>
          <button onClick={handlePayDeposit} className="cta-button">
            💳 Submit Payment Info
          </button>
          <button onClick={handleContactAgent} className="cta-button secondary">
            📞 Contact Agent
          </button>
          <button onClick={downloadPDF} className="cta-button secondary">
            📄 Download PDF
          </button>
        </div>

        {/* Agent Information */}
        <div className="agent-info">
          <div className="agent-avatar">
            {quote.agent_logo_url ? (
              <img 
                src={quote.agent_logo_url} 
                alt={`${quote.agent_business_name || quote.agent_name} Logo`} 
                className="agent-logo-avatar" 
              />
            ) : (
              quote.agent_name ? quote.agent_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'TA'
            )}
          </div>
          <div className="agent-name">{quote.agent_name || 'Your Travel Advisor'}</div>
          <div className="agent-title">Travel Specialist</div>
          {quote.agent_business_name && (
            <div className="agent-business">{quote.agent_business_name}</div>
          )}
          <p>Your dedicated travel professional ready to help make your journey unforgettable.</p>
          <div className="agent-contact">
            {quote.agent_email && (
              <a href={`mailto:${quote.agent_email}`} className="contact-item">
                <span className="contact-icon">📧</span>
                <span className="contact-text">{quote.agent_email}</span>
              </a>
            )}
            {quote.agent_phone && (
              <a href={`tel:${quote.agent_phone}`} className="contact-item">
                <span className="contact-icon">📞</span>
                <span className="contact-text">{quote.agent_phone}</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Contact Your Agent</h2>
            <p>Send a message to your travel agent about this quote:</p>
            <textarea
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
              placeholder="Please describe any questions or changes you'd like to make to your quote..."
              rows={6}
              disabled={sendingMessage}
            />
            <div className="modal-buttons">
              <button 
                onClick={() => setShowContactModal(false)}
                disabled={sendingMessage}
              >
                Cancel
              </button>
              <button 
                onClick={sendContactMessage} 
                className="btn-primary"
                disabled={sendingMessage || !contactMessage.trim()}
              >
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernQuoteView;
